import * as Sentry from '@sentry/react';
import type { MacroRuntimeMode } from '@playcastdotio/Messaging';
import type { MacroBundleSocketLoader } from '../../../services/playjector/MacroBundleSocketLoader';
import type { NativeMacroBridge } from '../../../services/playjector/NativeMacroBridge';

/**
 * Terminal result of one native launch. A failure names where it happened
 * (`bundle`: the load; `native`: Playjector's reply) and carries the code so
 * the campaign-switch failure reason can be specific.
 */
export type NativeLaunchOutcome =
  | { outcome: 'completed' }
  | { outcome: 'no_macro' }
  | { outcome: 'cancelled' }
  | { outcome: 'failed'; stage: 'bundle' | 'native'; code: string };
export type NativeLaunchBridge = Pick<NativeMacroBridge, 'start' | 'cancel'>;
export type NativeLaunchBundleLoader = Pick<MacroBundleSocketLoader, 'load' | 'cancel'>;

const COMPLETED: NativeLaunchOutcome = { outcome: 'completed' };
const NO_MACRO: NativeLaunchOutcome = { outcome: 'no_macro' };
const CANCELLED: NativeLaunchOutcome = { outcome: 'cancelled' };
const nativeFailure = (code: string): NativeLaunchOutcome => ({ outcome: 'failed', stage: 'native', code });

/**
 * Runs the campaign's locked `launch` tree natively: one bundle load, one
 * native start. There is no probe, no profile negotiation and no fallback —
 * a load error fails the launch with its code. A disabled revision completes
 * as `no_macro` with zero native calls.
 */
export class NativeMacroLaunchManager {
  private runId: string | null = null;
  private generation = 0;
  private cancellation: Promise<boolean> = Promise.resolve(true);
  private releaseConfirmed = true;

  public constructor(private readonly bridge: NativeLaunchBridge, private readonly bundles: NativeLaunchBundleLoader) {}

  public async start(campaignId: string, runId: string, mode: MacroRuntimeMode): Promise<NativeLaunchOutcome> {
    const generation = ++this.generation;
    try {
      if (!await this.cancellation || !this.releaseConfirmed) return nativeFailure('release_unconfirmed');
      if (generation !== this.generation) return CANCELLED;
      const loaded = await this.bundles.load(campaignId, 'launch');
      if (generation !== this.generation) return CANCELLED;
      // A disabled launch revision is a success: complete without touching
      // Playjector and without falling back to any legacy macro.
      if (loaded.kind === 'no_macro') return NO_MACRO;
      if (loaded.kind === 'error') {
        this.logBundleFailure(campaignId, runId, loaded.error);
        return { outcome: 'failed', stage: 'bundle', code: loaded.error };
      }
      this.runId = runId;
      const result = await this.bridge.start(runId, loaded.bundle, mode, loaded.assets);
      if (generation !== this.generation) return CANCELLED;
      if (mode === 'local-shadow') console.info('[MacroShadowObservations]', {
        runId, revision: loaded.bundle.revision, measurements: result.measurements ?? [],
      });
      if (result.outcome === 'completed') return COMPLETED;
      if (result.outcome === 'cancelled') return CANCELLED;
      return nativeFailure(result.errorCode ?? `native_run_${result.outcome}`);
    } catch (error) {
      if (generation !== this.generation) return CANCELLED;
      return nativeFailure(error instanceof Error && error.message ? error.message : 'exception');
    }
  }

  /** Cancels the in-flight run; resolves true only when every release so far was confirmed. */
  public cancel(): Promise<boolean> {
    ++this.generation;
    this.bundles.cancel();
    const runId = this.runId; this.runId = null;
    if (runId) {
      const previous = this.cancellation;
      const current = this.bridge.cancel(runId).catch(() => false);
      this.cancellation = Promise.all([previous, current]).then(([before, now]) => {
        this.releaseConfirmed = this.releaseConfirmed && before && now;
        return this.releaseConfirmed;
      });
    }
    return this.cancellation;
  }

  private logBundleFailure(campaignId: string, runId: string, error: string): void {
    const code = `native_macro_bundle_${error}`;
    console.error('[NativeMacroLaunchManager]', code, { campaignId, runId });
    try {
      if (Sentry.isInitialized()) Sentry.logger.error(code, { campaignId, runId, subsystem: 'macro' });
    } catch { /* Telemetry cannot interfere with the launch outcome. */ }
  }
}
